import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { useAuthStore } from "../store/auth";
import { toast } from "sonner";

interface ChatSession {
  id: string;
  widgetId: string;
  agentId?: string | null;
  status: "waiting" | "active" | "closed";
  startedAt: number;
  lastMessageAt?: number | null;
}

interface ChatSessionsResponse {
  sessions: ChatSession[];
}

interface RealtimeMessage {
  sessionId: string;
  content: string;
  senderType: string;
  agentId?: string;
  timestamp?: string;
}

export const ChatsPage = () => {
  const { agent } = useAuthStore();
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<RealtimeMessage[]>([]);
  const [draft, setDraft] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["chat-sessions"],
    queryFn: async () => {
      const response = await apiClient.get<ChatSessionsResponse>("/chats/sessions");
      return response.data.sessions;
    },
    refetchInterval: 10_000
  });

  const assignMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      await apiClient.post(`/chats/sessions/${sessionId}/assign`, {
        agentId: agent?.id
      });
    },
    onSuccess: () => {
      toast.success("Session claimed");
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Assignment failed")
  });

  const sendMutation = useMutation({
    mutationFn: async (payload: { sessionId: string; content: string }) => {
      await apiClient.post(`/chats/sessions/${payload.sessionId}/messages`, {
        content: payload.content
      });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Message not sent")
  });

  useEffect(() => {
    if (!selectedSession) return;

    const ws = new WebSocket(
      `${window.location.origin.replace(/^http/, "ws")}/api/chats/sessions/${selectedSession.id}/socket`
    );
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as RealtimeMessage;
        setMessages((prev) => [...prev, { ...data, timestamp: data.timestamp ?? new Date().toISOString() }]);
      } catch (error) {
        console.error("Failed to parse realtime payload", error);
      }
    };
    ws.onopen = () => {
      setMessages([]);
    };
    ws.onerror = () => {
      toast.error("Realtime connection interrupted");
    };


    return () => {
      ws.close();

    };
  }, [selectedSession?.id]);

  const handleSend = async () => {
    if (!selectedSession || !draft.trim()) return;
    setMessages((prev) => [
      ...prev,
      {
        sessionId: selectedSession.id,
        content: draft,
        senderType: "agent",
        agentId: agent?.id,
        timestamp: new Date().toISOString()
      }
    ]);
    setDraft("");
    await sendMutation.mutateAsync({ sessionId: selectedSession.id, content: draft });
  };

  const sessions = useMemo(() => data ?? [], [data]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-100">Live Conversations</h2>
        <p className="text-sm text-slate-400">
          Manage high-volume customer chats backed by Cloudflare Durable Objects and WebSockets.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <p className="text-sm font-medium text-slate-200">Open sessions</p>
            <p className="text-xs text-slate-500">Assign yourself to take over or monitor conversations.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-slate-400">No live sessions right now.</p>
            ) : (
              sessions.map((session) => {
                const isSelected = selectedSession?.id === session.id;
                const canAssign = !session.agentId || session.agentId === agent?.id;
                return (
                  <div
                    key={session.id}
                    className={`rounded-lg border px-4 py-3 transition ${
                      isSelected
                        ? "border-brand bg-brand/10"
                        : "border-slate-800 bg-slate-900/60 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-100">Session {session.id.slice(0, 8)}</p>
                        <p className="text-xs text-slate-500">Widget {session.widgetId}</p>
                      </div>
                      <Badge variant={session.status === "active" ? "success" : "info"}>
                        {session.status}
                      </Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Button
                        variant={isSelected ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => setSelectedSession(session)}
                      >
                        {isSelected ? "Viewing" : "View"}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!canAssign}
                        onClick={() => assignMutation.mutate(session.id)}
                      >
                        {session.agentId ? "Reassign" : "Take Session"}
                      </Button>
                    </div>
                    {session.agentId ? (
                      <p className="mt-2 text-xs text-slate-500">Agent: {session.agentId}</p>
                    ) : null}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card className="min-h-[480px]">
          <CardHeader>
            <p className="text-sm font-medium text-slate-200">Conversation</p>
            <p className="text-xs text-slate-500">
              {selectedSession ? `Streaming events for ${selectedSession.id}` : "Select a session to begin"}
            </p>
          </CardHeader>
          <CardContent className="flex h-full flex-col">
            <div className="flex-1 space-y-3 overflow-y-auto rounded-lg border border-slate-900 bg-slate-950 p-4">
              {selectedSession ? (
                messages.length === 0 ? (
                  <p className="text-sm text-slate-500">Waiting for messages�</p>
                ) : (
                  messages.map((message, index) => (
                    <div
                      key={`${message.timestamp}-${index}`}
                      className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${
                        message.senderType === "agent"
                          ? "ml-auto bg-brand text-white"
                          : message.senderType === "system"
                          ? "mx-auto bg-slate-800 text-slate-200"
                          : "bg-slate-800 text-slate-100"
                      }`}
                    >
                      <p>{message.content}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-white/60">
                        {new Date(message.timestamp ?? Date.now()).toLocaleTimeString()}
                      </p>
                    </div>
                  ))
                )
              ) : (
                <p className="text-sm text-slate-500">Choose a session to view the transcript.</p>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Input
                placeholder="Type a reply..."
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                disabled={!selectedSession}
              />
              <Button onClick={handleSend} disabled={!selectedSession || !draft.trim()}>
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};






